// Sicherheitsleiste nach der Anmeldung (docs/anmeldung/SPEZIFIKATION.md §6.7, §6.6, §6.2, §10.1): unter der Topbar,
// je Hinweis eine Zeile mit Sprung ins Profil – „Bitte richten Sie bis 30.10.2026 die Zwei-Faktor-Anmeldung ein“
// (Pflicht in der Frist), „Noch 2 Wiederherstellungscodes“, „3 Fehlversuche seit Ihrer letzten Anmeldung“. Quelle ist
// allein `session.security` (GET /api/session); ausgeblendete Hinweise kommen mit der nächsten Anmeldung wieder.
import { mfaDueText, RECOVERY_CODES, type SessionSecurity } from '@shared/auth'
import { formatDate } from '@shared/format'
import { businessDate } from '@shared/time'
import { KeyRound, ShieldAlert, UserRoundSearch, X, type LucideIcon } from 'lucide-react'
import { profileHref, type ProfileSection } from '../api/auth'
import { useSession } from '../api/session'
import { cn } from '../lib/cn'
import { dismissSecurityNotice, useSecurityNotices, type SecurityNotice } from '../stores/sessionNotices'
import { TONE_SUBTLE } from '../ui/Badge'
import { Button } from '../ui/Button'
import { IconButton } from '../ui/IconButton'

interface NoticeView {
  kind: SecurityNotice
  tone: 'warning' | 'info'
  icon: LucideIcon
  title: string
  text: string
  action: { label: string; section: ProfileSection }
}

/** Welche Hinweise gelten – reine Funktion über `session.security` (auch für Tests). */
export function securityNotices(security: SessionSecurity | undefined): NoticeView[] {
  if (!security) return []
  const notices: NoticeView[] = []
  if (security.mfa === 'off' && security.mfaSetupDueAt) {
    notices.push({
      kind: 'mfaDue',
      tone: 'warning',
      icon: ShieldAlert,
      title: mfaDueText(formatDate(businessDate(new Date(security.mfaSetupDueAt)))),
      text: 'Ihr Betrieb verlangt sie für Ihren Zugang. Danach ist die Anmeldung nur noch mit einem Code aus Ihrer App möglich.',
      action: { label: 'Jetzt einrichten', section: 'mfa' },
    })
  }
  const left = security.recoveryCodesLeft
  if (security.mfa === 'on' && left !== null && left <= RECOVERY_CODES.lowWarning) {
    notices.push({
      kind: 'recoveryLow',
      tone: left === 0 ? 'warning' : 'info',
      icon: KeyRound,
      title: left === 0 ? 'Sie haben keine Wiederherstellungscodes mehr.' : left === 1 ? 'Noch ein Wiederherstellungscode.' : `Noch ${left} Wiederherstellungscodes.`,
      text: 'Erzeugen Sie neue Codes, solange Sie Ihr Telefon zur Hand haben.',
      action: { label: 'Neue Codes erzeugen', section: 'mfa' },
    })
  }
  const failed = security.failedLoginsSinceLast
  if (failed > 0) {
    notices.push({
      kind: 'failedLogins',
      tone: 'info',
      icon: UserRoundSearch,
      title: failed === 1 ? 'Ein Fehlversuch seit Ihrer letzten Anmeldung.' : `${failed} Fehlversuche seit Ihrer letzten Anmeldung.`,
      text: 'Waren das nicht Sie? Prüfen Sie Ihre Geräte und ändern Sie im Zweifel Ihr Kennwort.',
      action: { label: 'Ansehen', section: 'activity' },
    })
  }
  return notices
}

const ICON_TONE = { warning: 'text-warning-fg', info: 'text-info-fg' } as const

export function SecurityBanner() {
  const session = useSession()
  const dismissed = useSecurityNotices((state) => state.dismissed)
  const notices = securityNotices(session?.security).filter((notice) => !dismissed.includes(notice.kind))
  if (notices.length === 0) return null
  return (
    <div className="shrink-0" data-testid="security-banner" data-print="hide">
      {notices.map((notice) => {
        const Icon = notice.icon
        return (
          <div
            key={notice.kind}
            role="status"
            data-testid="security-notice"
            data-kind={notice.kind}
            className={cn('flex flex-wrap items-start gap-x-3 gap-y-1.5 border-b px-4 py-2.5 max-md:px-3 max-md:py-2', TONE_SUBTLE[notice.tone], 'text-fg')}
          >
            <Icon className={cn('mt-0.5 size-4 shrink-0', ICON_TONE[notice.tone])} strokeWidth={1.75} aria-hidden />
            <div className="min-w-0 flex-1 basis-56">
              <p className="text-body font-medium text-fg touch:text-touch">{notice.title}</p>
              {/* am Telefon nur der Satz und die Handlung – die Erläuterung steht im Profil */}
              <p className="text-dense text-fg-muted max-md:hidden">{notice.text}</p>
            </div>
            <div className="flex shrink-0 items-center gap-1 max-md:ml-7">
              <Button size="sm" variant="secondary" href={profileHref(notice.action.section)} data-testid="security-notice-action">
                {notice.action.label}
              </Button>
              <IconButton icon={X} label="Hinweis ausblenden" size="sm" onClick={() => dismissSecurityNotice(notice.kind)} data-testid="security-notice-dismiss" />
            </div>
          </div>
        )
      })}
    </div>
  )
}
